// Fixed full-viewport canvas hosting the InkField. Scroll position of the
// hero + story sections drives the morph; the loop pauses whenever the stage
// is scrolled out of view or the tab is hidden.
import React, { useEffect, useRef, useState } from 'react';
import { InkField, isWebGLAvailable } from './inkField';
import './shared.css';

// Holds on each shape for a beat, morphs in between.
function storyCurve(p) {
  const seg = (v, a, b) => Math.min(1, Math.max(0, (v - a) / (b - a)));
  return 0.5 * seg(p, 0.1, 0.4) + 0.5 * seg(p, 0.55, 0.85);
}

export function InkStage({ storyRef, stageRef, onStep, onProgress }) {
  const wrapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    // Created per mount (not rendered by React) so a remount — e.g. StrictMode's
    // double effect in dev — never inherits the context destroy() just lost.
    const canvas = document.createElement('canvas');
    canvas.className = 'lp-canvas';
    wrap.prepend(canvas);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Without WebGL the story copy still steps on scroll; only the particles go.
    let field = null;
    if (isWebGLAvailable()) {
      try {
        field = new InkField(canvas, { reducedMotion: reduce });
      } catch (e) {
        field = null;
      }
    }
    if (!field) setFailed(true);

    let lastStep = -1;
    const update = () => {
      const story = storyRef.current;
      if (!story) return;
      const y = window.scrollY;
      const vh = window.innerHeight;
      const storyTop = story.getBoundingClientRect().top + y;
      const range = Math.max(1, story.offsetHeight - vh);
      const storyP = Math.min(1, Math.max(0, (y - storyTop) / range));
      if (onProgress) onProgress(storyP);
      let m;
      if (y < storyTop) {
        m = Math.min(1, Math.max(0, y / Math.max(1, storyTop)));
      } else {
        m = 1 + 2 * storyCurve(storyP);
      }
      if (field) field.setMorph(m);
      const step = m < 1.5 ? 0 : m < 2.5 ? 1 : 2;
      if (step !== lastStep) {
        lastStep = step;
        onStep(step);
      }
    };

    let scheduled = false;
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        update();
      });
    };
    const onPointer = (e) => {
      if (field) field.setPointer((e.clientX / window.innerWidth) * 2 - 1, -((e.clientY / window.innerHeight) * 2 - 1));
    };
    const onResize = () => {
      if (field) field.resize();
      update();
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('resize', onResize);

    let inView = true;
    const sync = () => {
      if (!field) return;
      if (inView && !document.hidden) field.start();
      else field.stop();
    };
    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      setVisible(inView);
      sync();
    });
    if (stageRef.current) io.observe(stageRef.current);
    document.addEventListener('visibilitychange', sync);
    sync();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', sync);
      io.disconnect();
      if (field) field.destroy();
      canvas.remove();
    };
  }, [storyRef, stageRef, onStep, onProgress]);

  return (
    <div ref={wrapRef} className={`lp-canvas-wrap ${visible ? '' : 'is-hidden'}`} aria-hidden="true">
      {failed && <div className="lp-canvas-fallback" />}
    </div>
  );
}

export default InkStage;
